import { Button, Header, Modal } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { Alert, TextField } from '@carsys/ui'
import { ChangeEvent, MutableRefObject, useRef, useState } from 'react'
import { Box, IconButton } from '@mui/material'
import { notifier } from '@cbgms/base/notifier/use-notifier'

export interface IAddAttachmentProps {
  open: boolean
  onCancel: () => void
  onConfirm: (attachmentFile: File, isEditing?: boolean) => Promise<void>
  isLoading: boolean
}

const ALLOWED_XML_TYPES = ['application/xml', 'text/xml']

export const AddAttachment = (props: IAddAttachmentProps) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')
  const [fileError, setFileError] = useState(false)
  const [fileHelperText, setFHText] = useState('')
  const [hideFileNameInput, setHideFNInput] = useState(false)

  const handleIconClick = () => {
    ;(fileInputRef as MutableRefObject<HTMLInputElement>).current.click()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files as FileList

    if (files.length > 0) {
      const file = files[0]
      const fileExtension = file.name.toLowerCase().split('.').pop()

      // Check file type by MIME type or extension
      if (!ALLOWED_XML_TYPES.includes(file.type) && fileExtension !== 'xml') {
        event.target.value = ''
        setFileErrorFn()
        return notifier.error(t('LKQConfig.file_type_error'))
      }

      if (file.size > 10000000) {
        event.target.value = ''
        setFileErrorFn()
        return notifier.error(t('LKQConfig.file_size_error'))
      }

      setFile(file)
      setFileName(file.name)
      setFileError(false)
      setFHText('')
    } else {
      setFileErrorFn()
    }
  }

  const setFileErrorFn = () => {
    setFile(null)
    setFileName('')
    setFileError(true)
    setFHText(t('email_attachment.add_attch_file_error'))
  }

  const reset = () => {
    setFile(null)
    setFileName('')
    setFileError(false)
    setFHText('')
    setHideFNInput(false)
  }

  const handleOnCancel = () => {
    reset()
    props.onCancel()
  }

  const handleOnConfirm = async () => {
    if (!file) return
    await props.onConfirm(file, true)
    reset()
  }

  return (
    <Modal open={props.open} size='small' data-testid='add-email-attachment-modal'>
      <Header content='XML upload' />
      <Modal.Content>
        <form noValidate>
          {!hideFileNameInput && (
            <Box
              style={{
                display: 'flex',
                width: '100%'
              }}
            >
              <TextField
                name='file'
                value={fileName}
                label={t('email_attachment.add_attch_file')}
                helperText={fileHelperText}
                error={fileError}
                required
                margin='normal'
                fullWidth
              />

              <IconButton color='primary' size='small' onClick={handleIconClick} data-testid='btn-upload-icon'>
                <i className='upload icon' />
              </IconButton>
            </Box>
          )}
          <input
            type='file'
            multiple={false}
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept='.xml,application/xml,text/xml'
            data-testid='btn-file-upload'
          />
        </form>

        <Alert severity='info'>Only XML files are allowed. Maximum file size: 10MB</Alert>
      </Modal.Content>
      <Modal.Actions>
        <Button content={t('global.cancel')} icon='cancel' onClick={handleOnCancel} data-testid='btn-cancel' />
        <Button
          content={t('global.confirm')}
          color='blue'
          onClick={handleOnConfirm}
          data-testid='btn-confirm'
          disabled={fileName.length === 0}
          loading={props.isLoading}
        />
      </Modal.Actions>
    </Modal>
  )
}
export default AddAttachment
